"use client"

import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { createClient } from "@/lib/supabase/client"
import { useAnchoredVisibility } from "@/hooks/use-anchored-visibility"
import type { User } from "@supabase/supabase-js"
import { Button } from "./ui/button"
import { Menu, Terminal, X } from "lucide-react"
import { OpenClawTerminal } from "./openclaw-terminal"
import {
  onSetupTerminal,
  type SetupTerminalRequest,
} from "@/lib/setup-terminal-event"

const TERMINAL_ANIMATION_MS = 280

const NAV_LINKS = [
  { href: "/characters", label: "Characters" },
  { href: "/skills", label: "Skills" },
]

const GLITCH_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()"

function GlitchText({ text }: { text: string }) {
  const [displayText, setDisplayText] = useState(text)
  const intervalRef = useRef<number | null>(null)

  useEffect(() => {
    setDisplayText(text)
  }, [text])

  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    let iteration = 0

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
    }

    intervalRef.current = window.setInterval(() => {
      setDisplayText(
        text
          .split("")
          .map((char, index) => {
            if (char === " ") return " "
            if (index < iteration) {
              return text[index]
            }
            return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]
          })
          .join("")
      )

      if (iteration >= text.length) {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        setDisplayText(text)
      }

      iteration += 1 / 3
    }, 30)
  }

  const handleMouseLeave = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setDisplayText(text)
  }

  return (
    <span
      data-glitch-text
      data-original={text}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="inline-block"
    >
      {displayText}
    </span>
  )
}

export function Navbar() {
  const pathname = usePathname()
  const [user, setUser] = useState<User | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [setupRequest, setSetupRequest] = useState<SetupTerminalRequest | null>(
    null
  )
  const [supabase] = useState(() => createClient())
  const desktopTerminalButtonRef = useRef<HTMLButtonElement | null>(null)
  const mobileTerminalButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data.user))
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null)
    })
    return () => subscription.unsubscribe()
  }, [supabase])

  useEffect(() => {
    return onSetupTerminal((req) => setSetupRequest(req))
  }, [])

  const isActivePath = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href)

  const navLinkClasses = (href: string) =>
    cn(
      "py-2 text-foreground transition-all duration-300 hover:scale-105",
      isActivePath(href) ? "nav-bar-underline" : "no-underline"
    )

  const terminal = useAnchoredVisibility({
    animationMs: TERMINAL_ANIMATION_MS,
    getAnchorRect: (target) => {
      const candidate =
        target instanceof HTMLElement
          ? target.getBoundingClientRect()
          : mobileOpen && mobileTerminalButtonRef.current
            ? mobileTerminalButtonRef.current.getBoundingClientRect()
            : (desktopTerminalButtonRef.current?.getBoundingClientRect() ??
              mobileTerminalButtonRef.current?.getBoundingClientRect() ??
              null)

      if (!candidate || candidate.width === 0 || candidate.height === 0) {
        return null
      }

      return candidate
    },
  })

  return (
    <header>
      {/* Spacer to offset fixed header */}
      <div className="h-20 md:h-24" />

      <div className="fixed top-0 right-0 left-0 z-[9999] h-20 w-full bg-background md:h-24">
        <div className="relative flex h-full items-center justify-center px-4 md:px-8">
          {/* Logo — left */}
          <Link
            href="/"
            className="absolute left-4 flex items-center gap-2 transition-transform duration-300 hover:scale-105 md:left-8"
          >
            {/* Mobile logo */}
            <div className="block md:hidden" style={{ lineHeight: 0 }}>
              <Image
                src="/logo_name.svg"
                alt="ego AI"
                width={80}
                height={80}
                className="h-auto w-auto"
              />
            </div>
            {/* Desktop logo */}
            <div className="hidden md:block" style={{ lineHeight: 0 }}>
              <Image
                src="/logo_name.svg"
                alt="ego AI"
                width={100}
                height={100}
                className="h-auto w-auto"
              />
            </div>
          </Link>

          {/* Center navigation — desktop */}
          <nav
            className="small-caps hidden items-center justify-center gap-12 tracking-wide lowercase md:flex md:text-xl"
            role="navigation"
            aria-label="Main navigation"
          >
            <Link href="/" className={navLinkClasses("/")}>
              <GlitchText text="Dashboard" />
            </Link>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={navLinkClasses(link.href)}
              >
                <GlitchText text={link.label} />
              </Link>
            ))}
          </nav>

          {/* Right side — auth actions */}
          <div className="absolute right-4 flex items-center gap-2 md:right-8">
            {/* Desktop terminal (auth handled by ego-world TopBar) */}
            <div className="hidden items-center gap-2 md:flex">
              {user ? (
                <Button
                  ref={desktopTerminalButtonRef}
                  variant="ghost"
                  size="sm"
                  onClick={(event) => terminal.toggle(event.currentTarget)}
                  className={cn(
                    "small-caps gap-2 text-base tracking-wide lowercase",
                    terminal.isVisible && "bg-primary/10 text-primary"
                  )}
                >
                  <Terminal className="h-4 w-4" />
                  Terminal
                </Button>
              ) : null}
            </div>

            {/* Mobile hamburger */}
            <button
              className="rounded-none p-2 text-foreground transition-colors hover:bg-muted md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="space-y-1 border-t border-border bg-background px-4 py-4 md:hidden">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "small-caps block rounded-none px-3 py-3 text-lg tracking-wide lowercase transition-colors",
                  pathname.startsWith(link.href)
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted"
                )}
              >
                <GlitchText text={link.label} />
              </Link>
            ))}
            <Link
              href="/"
              onClick={() => setMobileOpen(false)}
              className={cn(
                "small-caps block rounded-none px-3 py-3 text-lg tracking-wide lowercase transition-colors",
                isActivePath("/")
                  ? "bg-primary/10 text-primary"
                  : "text-foreground hover:bg-muted"
              )}
            >
              <GlitchText text="Dashboard" />
            </Link>
            {user ? (
              <button
                ref={mobileTerminalButtonRef}
                onClick={() => {
                  terminal.toggle(mobileTerminalButtonRef.current)
                  setMobileOpen(false)
                }}
                className={cn(
                  "small-caps block w-full rounded-none px-3 py-3 text-left text-lg tracking-wide lowercase transition-colors",
                  terminal.isVisible
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted"
                )}
              >
                <span className="flex items-center gap-2">
                  <Terminal className="h-4 w-4" />
                  <GlitchText text="Terminal" />
                </span>
              </button>
            ) : null}
          </div>
        )}

        {terminal.isMounted ? (
          <OpenClawTerminal
            visible={terminal.isVisible}
            originRect={terminal.anchorRect}
            animationMs={TERMINAL_ANIMATION_MS}
            onClose={terminal.close}
          />
        ) : null}

        {setupRequest ? (
          <OpenClawTerminal
            title={setupRequest.title}
            startDetached
            hideCatalog
            autoMessage={{
              agentId: setupRequest.agentId,
              text: setupRequest.message,
            }}
            onClose={() => setSetupRequest(null)}
          />
        ) : null}
      </div>
    </header>
  )
}
